/**
 * Invoice Management Types
 *
 * Type definitions for automated invoice generation from memo approvals
 */
export var InvoiceStatus;
(function (InvoiceStatus) {
    InvoiceStatus["DRAFT"] = "draft";
    InvoiceStatus["ISSUED"] = "issued";
    InvoiceStatus["PAID"] = "paid";
    InvoiceStatus["CANCELLED"] = "cancelled";
    InvoiceStatus["OVERDUE"] = "overdue";
})(InvoiceStatus || (InvoiceStatus = {}));
export var PaymentMethod;
(function (PaymentMethod) {
    PaymentMethod["WIRE_TRANSFER"] = "wire_transfer";
    PaymentMethod["CHECK"] = "check";
    PaymentMethod["CREDIT_CARD"] = "credit_card";
    PaymentMethod["ACH"] = "ach";
})(PaymentMethod || (PaymentMethod = {}));
// === HELPER FUNCTIONS (Level 3.2) ===
/**
 * Calculate total amount paid from payment summaries
 */
export function calculateAmountPaid(payments) {
    return payments
        .filter(p => p.status === 'cleared')
        .reduce((sum, payment) => sum + payment.amount, 0);
}
/**
 * Calculate amount due
 */
export function calculateAmountDue(total, amountPaid) {
    return Math.max(0, total - amountPaid);
}
/**
 * Determine if invoice is fully paid
 */
export function isInvoiceFullyPaid(invoice) {
    const amountPaid = invoice.amountPaid || 0;
    return amountPaid >= invoice.total;
}
/**
 * Get payment array with default
 */
export function getInvoicePayments(invoice) {
    return invoice.payments || [];
}
/**
 * Get amount paid with default
 */
export function getInvoiceAmountPaid(invoice) {
    return invoice.amountPaid || 0;
}
/**
 * Get amount due with default
 */
export function getInvoiceAmountDue(invoice) {
    return invoice.amountDue !== undefined ? invoice.amountDue : invoice.total;
}
/**
 * Determine if invoice is overdue
 */
export function isInvoiceOverdue(invoice) {
    if (invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.CANCELLED) {
        return false;
    }
    const dueDate = new Date(invoice.dueDate);
    const now = new Date();
    return now > dueDate;
}
/**
 * Get days until due (negative if overdue)
 */
export function getDaysUntilDue(invoice) {
    if (!invoice.dueDate)
        return null;
    const dueDate = new Date(invoice.dueDate);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}
export const INVOICE_STATUS_LABELS = {
    [InvoiceStatus.DRAFT]: 'Draft',
    [InvoiceStatus.ISSUED]: 'Issued',
    [InvoiceStatus.PAID]: 'Paid',
    [InvoiceStatus.CANCELLED]: 'Cancelled',
    [InvoiceStatus.OVERDUE]: 'Overdue'
};
export const INVOICE_STATUS_COLORS = {
    [InvoiceStatus.DRAFT]: '#6b7280',
    [InvoiceStatus.ISSUED]: '#3b82f6',
    [InvoiceStatus.PAID]: '#10b981',
    [InvoiceStatus.CANCELLED]: '#ef4444',
    [InvoiceStatus.OVERDUE]: '#f59e0b'
};
export const PAYMENT_METHOD_LABELS = {
    [PaymentMethod.WIRE_TRANSFER]: 'Wire Transfer',
    [PaymentMethod.CHECK]: 'Check',
    [PaymentMethod.CREDIT_CARD]: 'Credit Card',
    [PaymentMethod.ACH]: 'ACH Transfer'
};
// Default company information
export const DEFAULT_COMPANY_INFO = {
    companyName: 'Royal Gems, Inc.',
    companyAddress: '123 Diamond District\nNew York, NY 10036',
    companyPhone: '(555) 123-4567',
    companyEmail: 'invoices@royalgemsusa.com',
    companyTaxId: 'XX-XXXXXXX',
    bankingInfo: {
        bankName: 'Chase Bank',
        accountNumber: '****1234',
        routingNumber: '021000021',
        accountType: 'Business Checking',
        swiftCode: 'CHASUS33'
    }
};
// Default payment terms
export const DEFAULT_PAYMENT_TERMS = 'Net 30';
export const DEFAULT_INVOICE_NOTES = 'Thank you for your business. Payment is due within 30 days of invoice date.';
export const DEFAULT_INVOICE_TERMS = 'All sales are final. Late payments subject to 1.5% monthly interest charge.';
